import { useEffect, useRef, useState } from "react";
import { Check, RotateCcw, X, Trash2, Send, Loader2 } from "lucide-react";

const toBlob = (canvas) => new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));

// Sessione di scatto continua: le foto confermate restano in memoria e vengono inviate tutte insieme a fine sessione.
export const FotoCamera = ({ label, onSend, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const nativeRef = useRef(null);
  const [shots, setShots] = useState([]);
  const [pending, setPending] = useState(null);
  const [review, setReview] = useState(false);
  const [camErr, setCamErr] = useState("");
  const [sending, setSending] = useState(null);
  const [sendErr, setSendErr] = useState("");

  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  useEffect(() => {
    if (review) { stop(); return undefined; }
    let alive = true;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
        if (!alive) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
      } catch { setCamErr("Impossibile accedere alla fotocamera. Consenti l'accesso o usa lo scatto del telefono."); }
    })();
    return () => { alive = false; stop(); };
  }, [review]);
  useEffect(() => () => shots.forEach((s) => URL.revokeObjectURL(s.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const addBlob = (blob) => setPending({ blob, url: URL.createObjectURL(blob) });
  const shoot = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    addBlob(await toBlob(c));
  };
  const confirm = () => { setShots((s) => [...s, pending]); setPending(null); };
  const retake = () => { URL.revokeObjectURL(pending.url); setPending(null); };
  const remove = (i) => setShots((s) => { URL.revokeObjectURL(s[i].url); return s.filter((_, j) => j !== i); });
  const send = async () => {
    const files = shots.map((s, i) => new File([s.blob], `foto_${new Date().toISOString().replace(/[:.]/g, "-")}_${i + 1}.jpg`, { type: "image/jpeg" }));
    let sent = 0;
    setSending(0); setSendErr("");
    const ok = await onSend(files, (n) => { sent = n; setSending(n); });
    setSending(null);
    if (ok) { onClose(); return; }
    setShots((s) => { s.slice(0, sent).forEach((x) => URL.revokeObjectURL(x.url)); return s.slice(sent); });
    setSendErr(`Invio interrotto: ${sent} foto inviate. Le restanti sono ancora qui, riprova.`);
  };

  if (review) return (
    <div className="fixed inset-0 z-50 bg-slate-950 text-white flex flex-col" data-testid="foto-camera-review">
      <div className="p-4 flex items-center justify-between">
        <div><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div><div className="text-lg font-bold" data-testid="foto-review-count">{shots.length} foto pronte</div></div>
        <button type="button" onClick={() => setReview(false)} disabled={sending !== null} data-testid="foto-review-back" className="text-sm font-medium text-amber-400">Scatta altre</button>
      </div>
      <div className="flex-1 overflow-y-auto grid grid-cols-3 gap-2 px-4 content-start">
        {shots.map((s, i) => (
          <div key={s.url} className="relative aspect-square">
            <img src={s.url} alt="" className="h-full w-full object-cover rounded-lg" />
            <button type="button" onClick={() => remove(i)} disabled={sending !== null} data-testid={`foto-review-remove-${i}`} aria-label="Elimina" className="absolute top-1 right-1 rounded-full bg-black/60 p-1.5"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {sendErr && <p className="mx-4 rounded-lg bg-red-500/15 border border-red-500/40 px-3 py-2 text-sm text-red-200" role="alert" data-testid="foto-review-error">{sendErr}</p>}
      <div className="p-4 grid grid-cols-2 gap-3">
        <button type="button" onClick={() => { if (window.confirm("Annullare la sessione? Le foto scattate andranno perse.")) onClose(); }} disabled={sending !== null} data-testid="foto-review-cancel" className="rounded-2xl border border-slate-600 py-4 font-bold">Annulla</button>
        <button type="button" onClick={send} disabled={!shots.length || sending !== null} data-testid="foto-review-send" className="rounded-2xl bg-amber-500 text-slate-900 py-4 font-bold flex items-center justify-center gap-2 disabled:opacity-50">
          {sending !== null ? <><Loader2 className="h-5 w-5 animate-spin" /> {sending}/{shots.length}</> : <><Send className="h-5 w-5" /> Invia al gestionale</>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col" data-testid="foto-camera">
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} playsInline muted autoPlay className={`h-full w-full object-cover ${pending ? "hidden" : ""}`} data-testid="foto-camera-video" />
        {pending && <img src={pending.url} alt="Anteprima" className="h-full w-full object-contain" data-testid="foto-camera-preview" />}
        {camErr && !pending && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="text-sm" data-testid="foto-camera-error">{camErr}</p>
            <button type="button" onClick={() => nativeRef.current?.click()} data-testid="foto-camera-native" className="rounded-2xl bg-amber-500 text-slate-900 px-5 py-3 font-bold">Scatta con il telefono</button>
            <input ref={nativeRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="foto-camera-native-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) addBlob(f); e.target.value = ""; }} />
          </div>
        )}
        <div className="absolute top-0 inset-x-0 p-4 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
          <span className="text-sm font-bold">{label}</span>
          <span className="text-sm font-mono" data-testid="foto-camera-count">{shots.length} foto</span>
        </div>
      </div>
      {pending ? (
        <div className="p-5 grid grid-cols-2 gap-3 bg-black">
          <button type="button" onClick={retake} data-testid="foto-camera-retake" className="rounded-2xl border border-slate-600 py-4 font-bold flex items-center justify-center gap-2"><RotateCcw className="h-5 w-5" /> Riscatta</button>
          <button type="button" onClick={confirm} data-testid="foto-camera-confirm" className="rounded-2xl bg-emerald-500 text-slate-900 py-4 font-bold flex items-center justify-center gap-2"><Check className="h-5 w-5" /> Conferma</button>
        </div>
      ) : (
        <div className="p-5 flex items-center justify-between bg-black">
          <button type="button" onClick={() => { if (!shots.length || window.confirm("Uscire? Le foto scattate andranno perse.")) onClose(); }} data-testid="foto-camera-close" aria-label="Chiudi" className="h-12 w-12 rounded-full border border-slate-600 flex items-center justify-center"><X className="h-6 w-6" /></button>
          <button type="button" onClick={shoot} disabled={!!camErr} data-testid="foto-camera-shutter" aria-label="Scatta" className="h-20 w-20 rounded-full border-4 border-white bg-white/20 active:bg-white/60 disabled:opacity-30" />
          <button type="button" onClick={() => setReview(true)} disabled={!shots.length} data-testid="foto-camera-finish" className="h-12 px-4 rounded-full bg-amber-500 text-slate-900 font-bold disabled:opacity-40">Fine</button>
        </div>
      )}
    </div>
  );
};
